import Auth from '@aws-amplify/auth';
import axios from 'axios';
import { CognitoUserSession } from 'amazon-cognito-identity-js';
import { isEmpty } from 'lodash';
import store from '../store/';
import { User } from '../entities/user';

export enum Roles {
  user = "user",
  loanofficer = "loanofficer",
  branchmanager = "branchmanager",
  admin = "admin",
  superadmin = "superadmin",
  orgadmin = "orgadmin",
  textadmin = "textadmin"
}

class AuthService {

  static getUser(): User | null {

    const reduxStore: any = store.getState();
    const auth: any = reduxStore.auth.auth;

    if (isEmpty(reduxStore.auth.auth)) {
      return null;
    }

    return auth.user;

  }

  static getRoles(): string[] {
    const roles: string[] = [];
    const reduxStore: any = store.getState();
    const auth = (!isEmpty(reduxStore.auth.auth) && !isEmpty(reduxStore.auth.auth.user) ? reduxStore.auth.auth.user : null);

    if (!auth) {
      return [];
    }

    const parts = auth.role.split(',');
    parts.forEach((part: string) => {
      roles.push(part.trim());
    });

    return roles;

  }

  static isAuthorized(routeRoles: string[], path: string): boolean {

    try {

      const reduxStore: any = store.getState();
      const auth = (!isEmpty(reduxStore.auth.auth.user) ? reduxStore.auth.auth.user : null);

      if (!auth) {
        return false;
      }

      if (auth.status && ((auth.status === 'reset' && path !== '/auth/reset') || (auth.status === 'confirm' && path !== '/auth/confirm'))) {
        return false;
      }

      // const cognitoUser = await Auth.currentAuthenticatedUser();

      if (AuthService.checkUserRoles(auth.role, routeRoles)) {
        return true;
      }
      return false;

    } catch (error) {
      return false;
    }
  }

  static getRedirectRoute(path?: string): { pathname: string, search: string } {

    const loginRoute = '/auth/login';
    try {

      const reduxStore: any = store.getState();
      const auth = (!isEmpty(reduxStore.auth.auth) && !isEmpty(reduxStore.auth.auth.user) ? reduxStore.auth.auth.user : null);

      if (auth && auth.status === 'reset') {
        return {
          pathname: '/auth/reset',
          search: ''
        }
      }

      if (auth && auth.status === 'confirm') {
        return {
          pathname: '/auth/confirm',
          search: ''
        };
      }

      if (path) {
        return {
          pathname: loginRoute,
          search: `?redirect=${path}`
        };
      }

      return {
        pathname: loginRoute,
        search: ''
      };

    } catch (error) {
      return {
        pathname: loginRoute,
        search: ''
      };
    }

  }

  static checkUserRoles(userRoles: string, routeRoles: string[]): boolean {
    let hasRole = false;
    routeRoles.forEach((role: string) => {
      if (userRoles.indexOf(role) !== -1) {
        hasRole = true;
      }
    });
    return hasRole;
  }

  static refreshToken(): Promise<CognitoUserSession> {

    return new Promise(async (resolve, reject) => {

      try {

        const currentSession = await Auth.currentSession();
        const cognitoUser = await Auth.currentAuthenticatedUser();
        const refreshToken = currentSession.getRefreshToken();
        cognitoUser.refreshSession(refreshToken, (err: any, session: CognitoUserSession) => {

          if (err) {
            reject(err);
          } else {
            resolve(session);
          }

        });

      } catch (e) {
        if (e && e.code && e.code === 'PasswordResetRequiredException') {
          reject(e.code);
        } else {
          reject('NotAuthenticated');
        }
      }

    });

  }

  static createUser(email: string, password: string, name: string, phone: string, autoConfirm: boolean, forceReset: boolean): Promise<{ id: string }> {

    return new Promise(async (resolve, reject) => {

      try {

        // clean number
        phone = phone.replace(' ', '');
        phone = phone.replace('(', '');
        phone = phone.replace(')', '');
        phone = phone.replace('-', '');

        const data: any = {
          username: email,
          password,
          attributes: {
            name
          },
          clientMetadata: {
            autoConfirmUser: autoConfirm.toString()
          }
        };

        if (phone) {
          data.attributes.phone_number = `+1${phone}`;
        }

        const signupRes: any = await Auth.signUp(data);

        const userData = {
          userId: signupRes.userSub,
          email,
          name,
          phone,
          status: (forceReset ? 'reset' : 'active')
        };
        const userRes = await axios.post(`${AuthService.getApiUrls().user}/user`, userData);

        // send email
        const emailData = {
          to: email,
          templateName: 'galilaio_signup2',
          templateData: {
            username: email,
            password,
            url: 'https://dev.galilaio.com/auth/login'
          }
        };
        await axios.post(`${AuthService.getApiUrls().core}/sendemail`, emailData);


        resolve(userRes.data);

      } catch (e) {
        reject('Unable to create user');
      }

    });

  }


  static getApiUrls(): any {

    const stage = process.env.REACT_APP_STAGE || 'Dev';

    if (stage === 'Dev') {
      return {
        core: 'https://3pmu6xobkg.execute-api.us-west-2.amazonaws.com/dev',
        // core: 'http://localhost:4000',
        home: 'https://elsvt1aoxa.execute-api.us-west-2.amazonaws.com/dev',
        // home: 'http://localhost:4001',
        integrations: 'https://daiequr87h.execute-api.us-west-2.amazonaws.com/dev',
        // integrations: 'http://localhost:4002/dev',
        notifications: 'https://yihr62ec2d.execute-api.us-west-2.amazonaws.com/dev',
        // notifications: 'http://localhost:4003/dev',
        site: 'https://e4yhtugwme.execute-api.us-west-2.amazonaws.com/dev',
        // site: 'http://localhost:4004/dev',
        payment: ' https://rnf44z55z3.execute-api.us-west-2.amazonaws.com/dev',
        // payment: 'http://localhost:4005',
        user: 'https://v5m6mqvgs7.execute-api.us-west-2.amazonaws.com/dev',
        // user: 'http://localhost:4006/dev'
      }
    }

    return {
      core: 'https://3pmu6xobkg.execute-api.us-west-2.amazonaws.com/dev',
      home: 'https://elsvt1aoxa.execute-api.us-west-2.amazonaws.com/dev',
      integrations: 'https://daiequr87h.execute-api.us-west-2.amazonaws.com/dev',
      notifications: 'https://yihr62ec2d.execute-api.us-west-2.amazonaws.com/dev',
      site: 'https://e4yhtugwme.execute-api.us-west-2.amazonaws.com/dev',
      payment: ' https://rnf44z55z3.execute-api.us-west-2.amazonaws.com/dev',
      user: 'https://v5m6mqvgs7.execute-api.us-west-2.amazonaws.com/dev',
    }
  }
}

export default AuthService;



// WEBPACK FOOTER //
// ./src/services/auth.ts